// src/pages/FeesPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { collection, query, where, getDocs, addDoc, doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db } from "../firebase";
import Layout from "../components/Layout";
import { useStudent } from "../hooks/useStudent";
import { generateReceipt } from "../utils/generateReceipt";
import { FEE_DUE_DAY } from "../config";

const functions = getFunctions();

function toYM(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`; }
function fmtMonth(ym) {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${names[parseInt(m,10)-1]} ${y}`;
}
function fmtDate(d) {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
}
function fmtAmount(n) { return "₹" + Number(n||0).toLocaleString("en-IN"); }

export default function FeesPage() {
  const { student, course, batch } = useStudent();
  const [searchParams, setSearchParams] = useSearchParams();
  const [fees,      setFees]      = useState([]);
  const [eventFees, setEventFees] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [cfResult,  setCfResult]  = useState(null);

  const cfOrderId = searchParams.get('cf_order_id');

  const fetchFees = useCallback(async () => {
    // Query by the active profile's own doc ID, not the logged-in parent's auth
    // UID — for a sibling profile those differ, and querying by auth.uid would
    // silently return the primary account's fees instead of this profile's.
    if (!auth.currentUser || !student) { setLoading(false); return; }
    try {
      const snap = await getDocs(query(collection(db,"fees"), where("studentId","==",student.id)));
      setFees(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a,b) => (b.createdAt||"").localeCompare(a.createdAt||"")));
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [student]);

  const fetchEventFees = useCallback(async () => {
    if (!auth.currentUser || !student) return;
    const sid = student.id; // active profile's own doc ID — see fetchFees above
    try {
      const snap = await getDocs(query(collection(db,"eventFees"), where("status","==","active")));
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // A student's current enrolment start — fees created before this date belong
      // to a previous registration and must not follow a re-registered student.
      const enrolledAt = student.registeredAt || student.createdAt || null;

      const relevant = all.filter(ef => {
        // Hide fees that predate this student's current registration
        if (enrolledAt && ef.createdAt && ef.createdAt < enrolledAt) return false;
        // Check assignedStudentIds snapshot first — only students enrolled at creation time qualify
        if (ef.assignedStudentIds) return ef.assignedStudentIds.includes(sid);
        // Fallback for legacy event fees without snapshot — active students only
        if (ef.targetType === "all") return student?.status === "active";
        if (ef.targetType === "batch" && ef.targetId === student?.batchId) return true;
        if (ef.targetType === "student" && ef.targetId === sid) return true;
        return false;
      });
      const withStatus = await Promise.all(relevant.map(async ef => {
        const paySnap = await getDoc(doc(db,"eventFees",ef.id,"payments",sid));
        return { ...ef, alreadyPaid: paySnap.exists() };
      }));
      setEventFees(withStatus);
    } catch(e) { console.error("Event fees:", e); }
  }, [student]);

  useEffect(() => { fetchFees(); }, [fetchFees]);
  useEffect(() => { if (student) fetchEventFees(); }, [fetchEventFees, student]);

  // When Cashfree redirects back with cf_order_id in URL
  useEffect(() => {
    if (!cfOrderId) return;
    // Save BEFORE calling the function — so if the app is closed mid-verification,
    // the next time the student opens FeesPage it will retry automatically
    localStorage.setItem('pendingCfOrderId', cfOrderId);
    setSearchParams({}, { replace: true });
    // Push 2 buffer entries so the Cashfree payment URL is not reachable with a single back press
    window.history.pushState(null, '', '/fees');
    window.history.pushState(null, '', '/fees');
    setVerifying(true);
    httpsCallable(functions, 'confirmCashfreePayment')({ orderId: cfOrderId })
      .then(res => {
        localStorage.removeItem('pendingCfOrderId');
        if (res.data?.status === 'not_paid') setCfResult('failed');
        else { setCfResult('success'); fetchFees(); fetchEventFees(); }
      })
      .catch(() => setCfResult('failed')) // keep localStorage — retry on next open
      .finally(() => setVerifying(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfOrderId]);

  // On mount: retry any verification that was interrupted in a previous session
  useEffect(() => {
    const pending = localStorage.getItem('pendingCfOrderId');
    if (!pending || cfOrderId || !auth.currentUser) return;
    setVerifying(true);
    httpsCallable(functions, 'confirmCashfreePayment')({ orderId: pending })
      .then(res => {
        localStorage.removeItem('pendingCfOrderId');
        if (res.data?.status === 'not_paid') setCfResult('failed');
        else { setCfResult('success'); fetchFees(); fetchEventFees(); }
      })
      .catch(() => localStorage.removeItem('pendingCfOrderId'))
      .finally(() => setVerifying(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paid    = fees.filter(f => f.status === "approved");
  const pending = fees.filter(f => f.status === "pending_approval");
  const totalPaid = paid.reduce((s,f) => s + Number(f.amount||0), 0);

  const paidMonthSet    = useMemo(() => new Set(paid.filter(f=>f.feeType==="monthly").map(f=>f.forMonth).filter(Boolean)), [paid]);
  const pendingMonthSet = useMemo(() => new Set(pending.map(f=>f.forMonth).filter(Boolean)), [pending]);

  const now        = new Date();
  const currentYM  = toYM(now);
  const dayOfMonth = now.getDate();

  const dueMonths = useMemo(() => {
    if (batch?.feeType !== 'monthly') return [];
    const startYM = student?.feeStartDate ? String(student.feeStartDate).slice(0, 7) : currentYM;
    const result = [];
    const d = new Date(startYM + '-01');
    while (toYM(d) <= currentYM) {
      const ym = toYM(d);
      if (!paidMonthSet.has(ym) && !pendingMonthSet.has(ym)) result.push(ym);
      d.setMonth(d.getMonth() + 1);
    }
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch, student, paidMonthSet, pendingMonthSet, currentYM]);

  const getMonthAmount = useCallback((month) => {
    return student?.monthlyFeeOverrides?.[month] != null
      ? student.monthlyFeeOverrides[month]
      : (student?.feeOverride != null ? student.feeOverride : (batch?.fee || 0));
  }, [student, batch]);

  const totalDue = useMemo(() =>
    dueMonths.reduce((sum, m) => sum + Number(getMonthAmount(m)||0), 0),
    [dueMonths, getMonthAmount]
  );

  const thisMonthStatus = paidMonthSet.has(currentYM)
    ? { label:"✅ Paid",    color:"var(--green-text)", bg:"#f0fdf4" }
    : dayOfMonth > FEE_DUE_DAY
    ? { label:"🔴 Overdue", color:"var(--error-text)", bg:"#fef2f2" }
    : { label:"⏳ Due",     color:"var(--amber-text)", bg:"#fffbeb" };

  const unpaidEvents = eventFees.filter(ef => !ef.alreadyPaid);
  const paidEvents   = eventFees.filter(ef =>  ef.alreadyPaid);

  // Payment history grouped by fee type
  const monthlyPaid = paid.filter(f => f.feeType === "monthly");
  const eventPaid   = paid.filter(f => f.feeType === "event");
  const sessionPaid = paid.filter(f => f.feeType === "session");
  const eventGroups = useMemo(() => {
    const groups = {};
    eventPaid.forEach(f => {
      const key = f.eventFeeId || f.feeLabel || "event";
      if (!groups[key]) groups[key] = { label: f.feeLabel || "Event Fee", entries: [] };
      groups[key].entries.push(f);
    });
    return Object.values(groups);
  }, [eventPaid]);

  return (
    <Layout title="💰 My Fees" student={student}>

      {/* Payment verification banner */}
      {verifying && (
        <div style={{ background:"var(--surface-2)", border:"1px solid var(--border)", borderRadius:"10px", padding:"14px 18px", marginBottom:"20px", display:"flex", alignItems:"center", gap:"10px" }}>
          <span style={{ fontSize:"1.2rem" }}>⏳</span>
          <div style={{ fontWeight:"600", color:"var(--text-muted)" }}>Verifying your payment with Cashfree…</div>
        </div>
      )}
      {!verifying && cfResult === 'success' && (
        <div style={{ background:"var(--green-pale)", border:"1px solid rgba(22,163,74,.3)", borderRadius:"10px", padding:"14px 18px", marginBottom:"20px", display:"flex", alignItems:"center", gap:"10px" }}>
          <span style={{ fontSize:"1.4rem" }}>✅</span>
          <div>
            <div style={{ fontWeight:"700", color:"var(--green-text)" }}>Payment Successful!</div>
            <div style={{ fontSize:".82rem", color:"var(--green-text)" }}>Confirmed and recorded automatically. Thank you!</div>
          </div>
        </div>
      )}
      {!verifying && cfResult === 'failed' && (
        <div style={{ background:"var(--red-pale)", border:"1px solid rgba(239,68,68,.3)", borderRadius:"10px", padding:"14px 18px", marginBottom:"20px", display:"flex", alignItems:"center", gap:"10px" }}>
          <span style={{ fontSize:"1.4rem" }}>⚠️</span>
          <div>
            <div style={{ fontWeight:"700", color:"var(--error-text)" }}>Payment not confirmed</div>
            <div style={{ fontSize:".82rem", color:"var(--error-text)" }}>If money was deducted, contact your teacher with the order ID.</div>
          </div>
        </div>
      )}

      {/* ── Summary cards ── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"12px", marginBottom:"24px" }}>
        <SummaryCard label="Total Paid"  value={fmtAmount(totalPaid)} color="var(--purple-text)" bg="#FBF0E8"/>
        <SummaryCard label="Receipts"    value={paid.length}           color="var(--green-text)"  bg="#f0fdf4"/>
        {dueMonths.length > 0
          ? <SummaryCard label="Total Due" value={fmtAmount(totalDue)} color="#991b1b" bg="#fef2f2"/>
          : <SummaryCard label="This Month" value={thisMonthStatus.label} color={thisMonthStatus.color} bg={thisMonthStatus.bg}/>
        }
      </div>

      {/* ── Outstanding monthly fees ── */}
      {batch?.feeType === 'monthly' && dueMonths.length > 0 && (
        <div style={{ marginBottom:"20px" }}>
          {/* Total outstanding banner */}
          <div style={{ background:"#fef2f2", border:"2px solid #fca5a5", borderRadius:"12px", padding:"16px 20px", marginBottom:"14px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:"12px" }}>
            <div>
              <div style={{ fontWeight:"800", color:"#991b1b", fontSize:"1.05rem" }}>
                Total Outstanding: {fmtAmount(totalDue)}
              </div>
              <div style={{ fontSize:".82rem", color:"#7f1d1d", marginTop:"3px" }}>
                {dueMonths.length} month{dueMonths.length > 1 ? "s" : ""} unpaid
                {dueMonths.length > 1 ? " — pay each month separately below" : ""}
              </div>
            </div>
            <div style={{ background:"#991b1b", color:"white", padding:"5px 18px", borderRadius:"99px", fontSize:".85rem", fontWeight:"800", letterSpacing:".5px" }}>
              DUE
            </div>
          </div>
          {dueMonths.map(month => (
            <CashfreePayButton
              key={month}
              student={student}
              batch={batch}
              month={month}
              amount={getMonthAmount(month)}
              isOverdue={month < currentYM || (month === currentYM && dayOfMonth > FEE_DUE_DAY)}
            />
          ))}
        </div>
      )}

      {/* ── Pending approvals ── */}
      {pending.length > 0 && (
        <div className="card" style={{ marginBottom:"20px", border:"1px solid var(--border-warm,#fde68a)" }}>
          <div style={{ fontWeight:"700", color:"var(--amber-text)", marginBottom:"8px" }}>⏳ Awaiting Teacher Approval</div>
          {pending.map(f => (
            <div key={f.id} style={{ padding:"10px 0", borderBottom:"1px solid var(--border)", fontSize:".88rem" }}>
              <span style={{ fontWeight:"600" }}>{fmtAmount(f.amount)}</span>
              <span style={{ color:"var(--text-muted)", marginLeft:"10px" }}>
                {f.feeType==="monthly" ? fmtMonth(f.forMonth) : (f.feeLabel || "Event Fee")}
                {" · "}{fmtDate(f.date)}
                {f.mode && <span style={{ marginLeft:"6px", fontSize:".78rem", background:"var(--amber-pale)", color:"var(--amber-text)", padding:"1px 5px", borderRadius:"4px" }}>{f.mode}</span>}
              </span>
            </div>
          ))}
          <p style={{ fontSize:".78rem", color:"var(--amber-text)", marginTop:"10px" }}>
            Your teacher will confirm this and it will appear in history.
          </p>
        </div>
      )}

      {/* ── Event / special fees — grouped in one card ── */}
      {eventFees.length > 0 && (
        <div className="card" style={{ marginBottom:"20px", padding:"0", overflow:"hidden" }}>
          <div style={{ padding:"14px 18px", borderBottom:"1px solid var(--border-warm,#F5E4D0)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ fontWeight:"700", color:"var(--purple-text)" }}>🎪 Special Fee Requests</div>
            {unpaidEvents.length > 0 && (
              <span style={{ background:"#fef2f2", color:"#991b1b", border:"1px solid #fca5a5", padding:"2px 10px", borderRadius:"99px", fontSize:".78rem", fontWeight:"700" }}>
                {unpaidEvents.length} due
              </span>
            )}
          </div>
          {unpaidEvents.map(ef => (
            <EventFeeRow key={ef.id} eventFee={ef} student={student} onPaid={() => { fetchEventFees(); fetchFees(); }} />
          ))}
          {paidEvents.map(ef => (
            <div key={ef.id} style={{ padding:"12px 18px", borderTop:"1px solid var(--border)", display:"flex", alignItems:"center", gap:"12px" }}>
              <div style={{ fontSize:"1.1rem" }}>✅</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:"600", color:"var(--green-text)", fontSize:".9rem" }}>{ef.title}</div>
                <div style={{ fontSize:".75rem", color:"var(--text-muted)" }}>Paid — {fmtAmount(ef.amount)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Cash / manual payment ── */}
      {(batch?.feeType !== 'monthly' || dueMonths.length > 0) && (
        <ManualPaymentForm student={student} batch={batch} dueMonths={dueMonths} currentYM={currentYM} onSubmitted={fetchFees} />
      )}

      {/* ── Payment history — grouped by type ── */}
      <div className="card">
        <h2 style={{ margin:"0 0 4px", borderBottom:"none", paddingBottom:0 }}>🧾 Payment History</h2>
        <p style={{ fontSize:".78rem", color:"var(--text-muted)", marginBottom:"16px" }}>
          {paid.length} confirmed payment{paid.length !== 1 ? "s" : ""} · Total: <strong>{fmtAmount(totalPaid)}</strong>
        </p>
        {loading ? (
          <p className="muted">Loading…</p>
        ) : paid.length === 0 ? (
          <div style={{ textAlign:"center", padding:"32px 0" }}>
            <div style={{ fontSize:"2.5rem", marginBottom:"8px" }}>🧾</div>
            <p className="muted">No confirmed payments yet.</p>
          </div>
        ) : (
          <div>
            {monthlyPaid.length > 0 && (
              <div style={{ marginBottom:eventGroups.length > 0 || sessionPaid.length > 0 ? "20px" : "0" }}>
                <div style={{ fontSize:".72rem", fontWeight:"700", color:"var(--text-muted)", letterSpacing:".06em", textTransform:"uppercase", marginBottom:"8px" }}>
                  📅 Monthly Fees
                </div>
                {monthlyPaid.map((f,i) => (
                  <FeeHistoryRow key={f.id} fee={f} isLast={i===monthlyPaid.length-1} student={student} course={course} batch={batch} />
                ))}
              </div>
            )}
            {eventGroups.length > 0 && (
              <div style={{ marginBottom:sessionPaid.length > 0 ? "20px" : "0" }}>
                <div style={{ fontSize:".72rem", fontWeight:"700", color:"var(--text-muted)", letterSpacing:".06em", textTransform:"uppercase", marginBottom:"8px" }}>
                  🎪 Special Fees
                </div>
                {eventGroups.map((group, gi) =>
                  group.entries.map((f,i) => (
                    <FeeHistoryRow key={f.id} fee={f} isLast={i===group.entries.length-1 && gi===eventGroups.length-1} student={student} course={course} batch={batch} />
                  ))
                )}
              </div>
            )}
            {sessionPaid.length > 0 && (
              <div>
                <div style={{ fontSize:".72rem", fontWeight:"700", color:"var(--text-muted)", letterSpacing:".06em", textTransform:"uppercase", marginBottom:"8px" }}>
                  🎯 Session Fees
                </div>
                {sessionPaid.map((f,i) => (
                  <FeeHistoryRow key={f.id} fee={f} isLast={i===sessionPaid.length-1} student={student} course={course} batch={batch} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

function SummaryCard({ label, value, color, bg }) {
  return (
    <div style={{ background:"var(--surface-2)", borderRadius:"12px", padding:"14px 10px", textAlign:"center", border:"1px solid var(--border)" }}>
      <div style={{ fontSize:"1.25rem", fontWeight:"800", color, lineHeight:1.2 }}>{value}</div>
      <div style={{ fontSize:".75rem", color:"var(--text-muted)", marginTop:"4px" }}>{label}</div>
    </div>
  );
}

function CashfreePayButton({ student, batch, month, amount, isOverdue }) {
  const [paying, setPaying] = useState(false);
  const [error,  setError]  = useState('');

  const handlePay = useCallback(async () => {
    setError(''); setPaying(true);
    try {
      const { load } = await import('@cashfreepayments/cashfree-js');
      const cashfree = await load({ mode: 'production' });
      const createOrder = httpsCallable(functions, 'createCashfreeOrder');
      const result = await createOrder({ studentId: student.id, amount: Number(amount), month, batchId: student.batchId || '' });
      cashfree.checkout({ paymentSessionId: result.data.paymentSessionId, redirectTarget: '_self' });
    } catch(e) {
      setError(e.message || 'Payment failed. Please try again.');
      setPaying(false);
    }
  }, [student, amount, month]);

  const hasMonthOverride   = student?.monthlyFeeOverrides?.[month] != null;
  const hasStudentOverride = !hasMonthOverride && student?.feeOverride != null;

  return (
    <div className="card" style={{ marginBottom:"12px", border: isOverdue ? "1px solid #fca5a5" : "1px solid var(--border-warm,#F5E4D0)" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:"12px" }}>
        <div>
          <div style={{ fontWeight:"700", fontSize:".95rem", color: isOverdue ? "var(--error-text)" : "var(--purple-text)" }}>
            {isOverdue ? "🔴" : "💰"} {fmtMonth(month)} — {isOverdue ? "Overdue" : "Due"}
          </div>
          <div style={{ color:"var(--text-muted)", fontSize:".88rem", marginTop:"3px", display:"flex", alignItems:"center", gap:"8px" }}>
            <strong style={{ color: isOverdue ? "#991b1b" : "var(--purple-text)", fontSize:"1rem" }}>{fmtAmount(amount)}</strong>
            {hasMonthOverride  && <span style={{ fontSize:".72rem", background:"#fef3c7", color:"var(--amber-text)", padding:"1px 6px", borderRadius:"4px" }}>📉 Partial classes</span>}
            {hasStudentOverride && <span style={{ fontSize:".72rem", background:"#fef3c7", color:"var(--amber-text)", padding:"1px 6px", borderRadius:"4px" }}>🎓 Scholarship rate</span>}
          </div>
          {!isOverdue && <div style={{ fontSize:".72rem", color:"var(--amber-text)", marginTop:"2px" }}>Due by {FEE_DUE_DAY}th of this month</div>}
        </div>
        <button onClick={handlePay} disabled={paying}
          style={{ background: paying ? "#9ca3af" : isOverdue ? "#991b1b" : "#7B1515", color:"white", padding:"10px 20px", borderRadius:"8px", border:"none", fontWeight:"700", fontSize:".9rem", cursor: paying ? "not-allowed" : "pointer" }}>
          {paying ? "Opening…" : "💳 Pay Now"}
        </button>
      </div>
      {error && <p style={{ fontSize:".78rem", color:"var(--error-text)", marginTop:"8px" }}>{error}</p>}
      <p style={{ fontSize:".75rem", color:"var(--text-muted)", marginTop:"8px" }}>
        Pay via UPI, debit/credit card, or net banking. Confirmed automatically — no screenshot needed.
      </p>
    </div>
  );
}

function EventFeeRow({ eventFee, student, onPaid }) {
  const [cashOpen,   setCashOpen]   = useState(false);
  const [cashMode,   setCashMode]   = useState('Cash');
  const [cashNote,   setCashNote]   = useState('');
  const [cashSaving, setCashSaving] = useState(false);
  const [paying,     setPaying]     = useState(false);
  const [error,      setError]      = useState('');

  const isPast = eventFee.dueDate && eventFee.dueDate < new Date().toISOString().slice(0,10);

  const handlePay = useCallback(async () => {
    setError(''); setPaying(true);
    try {
      const { load } = await import('@cashfreepayments/cashfree-js');
      const cashfree = await load({ mode: 'production' });
      const createOrder = httpsCallable(functions, 'createCashfreeOrder');
      const result = await createOrder({ studentId: student.id, amount: Number(eventFee.amount), feeType: 'event', eventFeeId: eventFee.id, feeLabel: eventFee.title, batchId: student.batchId || '' });
      cashfree.checkout({ paymentSessionId: result.data.paymentSessionId, redirectTarget: '_self' });
    } catch(e) {
      setError(e.message || 'Payment failed.');
      setPaying(false);
    }
  }, [student, eventFee]);

  async function handleCashSubmit(e) {
    e.preventDefault(); setCashSaving(true);
    try {
      await addDoc(collection(db,'fees'), {
        studentId: student.id, amount: Number(eventFee.amount),
        feeType: 'event', eventFeeId: eventFee.id, feeLabel: eventFee.title,
        date: new Date().toISOString().slice(0,10), mode: cashMode, note: cashNote,
        status: 'pending_approval', submittedByStudent: true,
        createdAt: new Date().toISOString(), submittedAt: new Date().toISOString(),
      });
      try {
        await httpsCallable(functions,'notifyAdmins')({
          title: '💰 Cash Payment Reported',
          body: `${student?.name||'A student'} reported ₹${eventFee.amount} ${cashMode} for ${eventFee.title}`,
        });
      } catch(_) {}
      setCashOpen(false); onPaid();
    } catch(e) { alert('Failed: ' + e.message); }
    setCashSaving(false);
  }

  return (
    <div style={{ borderTop:"1px solid var(--border-warm,#F5E4D0)" }}>
      <div style={{ padding:"14px 18px", display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:"12px" }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:"8px", flexWrap:"wrap", marginBottom:"3px" }}>
            <div style={{ fontWeight:"700", color: isPast ? "var(--error-text)" : "var(--purple-text)", fontSize:".95rem" }}>
              {eventFee.title}
            </div>
            <span style={{ background: isPast ? "#fef2f2" : "#fffbeb", color: isPast ? "#991b1b" : "#92400e",
              border:`1px solid ${isPast ? "#fca5a5" : "#fde68a"}`,
              padding:"1px 7px", borderRadius:"99px", fontSize:".72rem", fontWeight:"700" }}>
              {isPast ? "🔴 Overdue" : "🟡 Due"}
            </span>
          </div>
          {eventFee.description && <div style={{ fontSize:".8rem", color:"var(--text-muted)", marginBottom:"2px" }}>{eventFee.description}</div>}
          <div style={{ fontSize:".88rem" }}>
            <strong style={{ color: isPast ? "#991b1b" : "var(--purple-text)" }}>{fmtAmount(eventFee.amount)}</strong>
            {eventFee.dueDate && <span style={{ color:"var(--text-muted)", marginLeft:"10px", fontSize:".78rem" }}>Due: {fmtDate(eventFee.dueDate)}</span>}
          </div>
        </div>
        <div style={{ display:"flex", gap:"8px", flexShrink:0 }}>
          <button onClick={() => setCashOpen(o=>!o)}
            style={{ background:"none", border:"1px solid #F5E4D0", color:"var(--purple-text)", padding:"7px 12px", borderRadius:"8px", fontWeight:"600", fontSize:".82rem", cursor:"pointer" }}>
            💵 Cash
          </button>
          <button onClick={handlePay} disabled={paying}
            style={{ background: paying ? "#9ca3af" : isPast ? "#991b1b" : "#7B1515", color:"white", padding:"7px 14px", borderRadius:"8px", border:"none", fontWeight:"700", fontSize:".82rem", cursor: paying ? "not-allowed" : "pointer" }}>
            {paying ? "…" : "💳 Pay"}
          </button>
        </div>
      </div>
      {cashOpen && (
        <form onSubmit={handleCashSubmit} style={{ margin:"0 18px 14px", padding:"12px 14px", background:"var(--surface-2)", borderRadius:"8px", border:"1px solid #F5E4D0" }}>
          <div style={{ fontWeight:"600", fontSize:".85rem", color:"var(--purple-text)", marginBottom:"8px" }}>Report Cash / Bank Transfer</div>
          <div style={{ display:"flex", gap:"12px", marginBottom:"8px", flexWrap:"wrap" }}>
            {["Cash","Bank Transfer","Cheque","Other"].map(m => (
              <label key={m} style={{ display:"flex", alignItems:"center", gap:"4px", cursor:"pointer", fontSize:".85rem" }}>
                <input type="radio" name={`efMode-${eventFee.id}`} value={m} checked={cashMode===m} onChange={() => setCashMode(m)} />
                {m}
              </label>
            ))}
          </div>
          <input type="text" value={cashNote} onChange={e=>setCashNote(e.target.value)}
            placeholder="Note (optional)" style={{ width:"100%", padding:"7px", border:"1px solid #F5E4D0", borderRadius:"6px", fontSize:".83rem", marginBottom:"8px", boxSizing:"border-box" }} />
          <div style={{ display:"flex", gap:"8px" }}>
            <button type="button" onClick={() => setCashOpen(false)}
              style={{ background:"none", border:"1px solid #E5E7EB", color:"#6B7280", padding:"7px 12px", borderRadius:"6px", cursor:"pointer", fontSize:".82rem" }}>Cancel</button>
            <button type="submit" disabled={cashSaving}
              style={{ background: cashSaving ? "#9ca3af" : "#7B1515", color:"white", padding:"7px 16px", borderRadius:"6px", border:"none", fontWeight:"700", fontSize:".82rem", cursor:"pointer" }}>
              {cashSaving ? "Submitting…" : "✅ Submit"}
            </button>
          </div>
        </form>
      )}
      {error && <p style={{ fontSize:".78rem", color:"var(--error-text)", padding:"0 18px 10px" }}>{error}</p>}
    </div>
  );
}

function FeeHistoryRow({ fee: f, isLast, student, course, batch }) {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"11px 0", borderBottom: isLast ? "none" : "1px solid var(--border-warm)", gap:"10px" }}>
      <div style={{ flex:1, minWidth:0 }}>
        {f.feeType === "monthly"
          ? <span style={{ background:"var(--surface-2)", color:"var(--purple-text)", border:"1px solid var(--border-warm)", padding:"2px 8px", borderRadius:"99px", fontSize:".78rem", fontWeight:"600", whiteSpace:"nowrap" }}>📅 {fmtMonth(f.forMonth)}</span>
          : f.feeType === "event"
          ? <span style={{ background:"var(--amber-pale)", color:"var(--amber-text)", padding:"2px 8px", borderRadius:"99px", fontSize:".78rem", fontWeight:"600", whiteSpace:"nowrap" }}>🎪 {f.feeLabel||"Event Fee"}</span>
          : <span style={{ background:"var(--surface-2)", color:"var(--text-muted)", border:"1px solid var(--border)", padding:"2px 8px", borderRadius:"99px", fontSize:".78rem", fontWeight:"600", whiteSpace:"nowrap" }}>🎯 Session</span>
        }
        {f.feeType === "session" && f.date && <div style={{ fontSize:".72rem", color:"var(--text-muted)", marginTop:"2px" }}>{fmtDate(f.date)}</div>}
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:"8px", flexShrink:0 }}>
        <span style={{ fontWeight:"700", color:"var(--purple-text)", fontSize:".92rem" }}>{fmtAmount(f.amount)}</span>
        {f.mode && <span style={{ background:"var(--input-bg)", color:"var(--text-muted)", padding:"2px 6px", borderRadius:"4px", fontSize:".72rem" }}>{f.mode}</span>}
        {f.receiptNo && (
          <button
            onClick={async () => {
              try { await generateReceipt({ ...f, studentName: student?.name, studentSubject: student?.subject, courseName: course?.name, batchName: batch?.name, batchTiming: batch?.timing||batch?.schedule||batch?.time }); }
              catch(e) { alert("Could not generate receipt: " + e.message); }
            }}
            style={{ background:"none", border:"1px solid var(--border-warm)", color:"var(--purple-text)", padding:"4px 8px", borderRadius:"6px", cursor:"pointer", fontSize:".72rem", fontWeight:"600", whiteSpace:"nowrap" }}>
            📄 Receipt
          </button>
        )}
      </div>
    </div>
  );
}

function ManualPaymentForm({ student, batch, dueMonths, currentYM, onSubmitted }) {
  const [open,   setOpen]   = useState(false);
  const [month,  setMonth]  = useState('');
  const [amount, setAmount] = useState('');
  const [mode,   setMode]   = useState('Cash');
  const [note,   setNote]   = useState('');
  const [saving, setSaving] = useState(false);
  const [done,   setDone]   = useState(false);

  const monthOptions = useMemo(() => (dueMonths?.length > 0 ? dueMonths : [currentYM]), [dueMonths, currentYM]);
  useEffect(() => { if (!month && monthOptions.length > 0) setMonth(monthOptions[0]); }, [month, monthOptions]);
  useEffect(() => {
    const amt = student?.feeOverride != null ? student.feeOverride : batch?.fee;
    if (amt && !amount) setAmount(String(amt));
  }, [student, batch, amount]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await addDoc(collection(db,'fees'), {
        studentId: student.id, amount: Number(amount),
        feeType: 'monthly', forMonth: month, month,
        date: new Date().toISOString().slice(0,10), mode, note,
        status: 'pending_approval', submittedByStudent: true,
        createdAt: new Date().toISOString(), submittedAt: new Date().toISOString(),
      });
      try {
        await httpsCallable(functions,'notifyAdmins')({
          title: '💰 Manual Payment Reported',
          body: `${student?.name||'A student'} reported ₹${amount} ${mode} for ${fmtMonth(month)}`,
        });
      } catch(_) {}
      setDone(true); onSubmitted();
    } catch(e) { alert('Failed to submit: ' + e.message); }
    setSaving(false);
  };

  if (done) return (
    <div className="card" style={{ marginBottom:"20px", background:"#f0fdf4", border:"1px solid #86efac" }}>
      <div style={{ fontWeight:"700", color:"var(--green-text)" }}>✅ Payment reported to your teacher!</div>
      <div style={{ fontSize:".82rem", color:"var(--green-text)", marginTop:"4px" }}>It will appear in history once confirmed.</div>
    </div>
  );

  return (
    <div className="card" style={{ marginBottom:"20px" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", cursor:"pointer" }} onClick={() => setOpen(o=>!o)}>
        <div style={{ fontWeight:"700", color:"var(--purple-text)" }}>💵 Paid by Cash or Bank Transfer?</div>
        <span style={{ color:"var(--text-muted)", fontSize:".9rem" }}>{open ? "▲ Hide" : "▼ Report"}</span>
      </div>
      {open && (
        <form onSubmit={handleSubmit} style={{ marginTop:"16px" }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px", marginBottom:"12px" }}>
            <div>
              <label style={{ fontSize:".8rem", fontWeight:"600", color:"var(--text-label)", display:"block", marginBottom:"4px" }}>For Month *</label>
              <select value={month} onChange={e=>setMonth(e.target.value)} required
                style={{ width:"100%", padding:"8px", border:"1px solid #F5E4D0", borderRadius:"6px", fontSize:".88rem" }}>
                {monthOptions.map(m => <option key={m} value={m}>{fmtMonth(m)}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:".8rem", fontWeight:"600", color:"var(--text-label)", display:"block", marginBottom:"4px" }}>Amount (₹) *</label>
              <input type="number" value={amount} onChange={e=>setAmount(e.target.value)} required min="1" max="100000"
                style={{ width:"100%", padding:"8px", border:"1px solid #F5E4D0", borderRadius:"6px", fontSize:".88rem" }} />
            </div>
          </div>
          <div style={{ marginBottom:"12px" }}>
            <label style={{ fontSize:".8rem", fontWeight:"600", color:"var(--text-label)", display:"block", marginBottom:"6px" }}>Payment Mode *</label>
            <div style={{ display:"flex", gap:"16px", flexWrap:"wrap" }}>
              {["Cash","Bank Transfer","Cheque","Other"].map(m => (
                <label key={m} style={{ display:"flex", alignItems:"center", gap:"5px", cursor:"pointer", fontSize:".88rem" }}>
                  <input type="radio" name="payMode" value={m} checked={mode===m} onChange={() => setMode(m)} />
                  {m}
                </label>
              ))}
            </div>
          </div>
          <div style={{ marginBottom:"14px" }}>
            <label style={{ fontSize:".8rem", fontWeight:"600", color:"var(--text-label)", display:"block", marginBottom:"4px" }}>Note (optional)</label>
            <input type="text" value={note} onChange={e=>setNote(e.target.value)}
              placeholder="e.g. paid during class on 5th"
              style={{ width:"100%", padding:"8px", border:"1px solid #F5E4D0", borderRadius:"6px", fontSize:".88rem" }} />
          </div>
          <button type="submit" disabled={saving}
            style={{ background: saving ? "#9ca3af" : "#7B1515", color:"white", padding:"10px 22px", borderRadius:"8px", border:"none", fontWeight:"700", fontSize:".9rem", cursor: saving ? "not-allowed" : "pointer" }}>
            {saving ? "Submitting…" : "✅ Submit for Approval"}
          </button>
          <p style={{ fontSize:".75rem", color:"var(--text-muted)", marginTop:"8px" }}>
            Your teacher will review and confirm. Fee appears in history once approved.
          </p>
        </form>
      )}
    </div>
  );
}
